import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, ArrowDownIcon } from '@phosphor-icons/react';
import ExploreCard from './ExploreCard';
import usePromotionPacks from './usePromotionPacks';

const ExploreTestSeries = () => {
  const { packs, loading, setReminder } = usePromotionPacks();
  const scrollContainerRef = useRef(null);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  // Shadow states
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Shadow logic (copied exactly from ExploreSection)
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;

    if (!isScrollable) {
      setShowLeftShadow(false);
      setShowRightShadow(false);
      return;
    }

    setShowLeftShadow(scrollLeft > 1);
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);
      const resizeObserver = new ResizeObserver(handleScroll);
      resizeObserver.observe(container);

      return () => {
        container.removeEventListener('scroll', handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [packs]);

  if (loading) {
    return (
      <div className="px-4 md:px-2">
        <div className="text-2xl font-bold text-[var(--q3-neutral-default)] mb-6">Explore Other Test Series</div>
        <div className="flex space-x-4 overflow-hidden">
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              className="flex-shrink-0 w-[360px] h-[400px] rounded-xl bg-[var(--q3-surface-default)] animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-2">
      <div className="hidden md:block">
        <div className="flex items-center space-x-3 mb-6">
          <div className="text-2xl font-bold text-[var(--q3-neutral-default)]">Explore Other Test Series</div>

          <div className="flex space-x-2">
            <button
              onClick={() => scroll('left')}
              className="
                flex items-center rounded-[6.667px]
                border border-[var(--q3-stroke-normal)]
                bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]
                transition-colors px-2 py-1 gap-0 self-stretch cursor-pointer
              "
            >
              <ArrowLeftIcon size={20} color="var(--q3-neutral-default)" />
            </button>

            <button
              onClick={() => scroll('right')}
              className="
                flex items-center rounded-[6.667px]
                border border-[var(--q3-stroke-normal)]
                bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)]
                transition-colors px-2 py-1 gap-0 self-stretch cursor-pointer
              "
            >
              <ArrowRightIcon size={20} color="var(--q3-neutral-default)" />
            </button>
          </div>
        </div>

        {/* Shadow container wrapper */}
        <div className="relative">
          {/* LEFT shadow */}
          {showLeftShadow && (
            <div
              className="absolute left-0 top-0 bottom-0 w-8 z-10 pointer-events-none 
              [background:linear-gradient(to_right,var(--q3-surface-default),transparent)]"
            />
          )}

          {/* RIGHT shadow */}
          {showRightShadow && (
            <div
              className="absolute right-0 top-0 bottom-0 w-8 z-10 pointer-events-none 
              [background:linear-gradient(to_left,var(--q3-surface-default),transparent)]"
            />
          )}

          {/* Scroll Container */}
          <div
            ref={scrollContainerRef}
            className="flex space-x-6 overflow-x-auto scrollbar-hide scroll-smooth py-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {packs.map((pack) => (
              <ExploreCard key={pack._id} pack={pack} onSetReminder={setReminder} />
            ))}
          </div>
        </div>
      </div>

      <div className="md:hidden">
        <div className="text-xl font-bold text-[var(--q3-neutral-default)] mb-4">Explore Other Test Series</div>

        <div className="space-y-4 mb-4 relative">
          {packs.slice(0, 2).map((pack) => (
            <ExploreCard key={pack._id} pack={pack} onSetReminder={setReminder} isMobile={true} />
          ))}

          {!isAccordionOpen && packs.length > 2 && (
            <div className="absolute bottom-0 left-0 w-full  bg-gradient-to-t from-white/90 to-transparent backdrop-blur-md pointer-events-none"></div>
          )}
        </div>

        {isAccordionOpen && (
          <div className="space-y-4 mb-4">
            {packs.slice(2).map((pack) => (
              <ExploreCard key={pack._id} pack={pack} onSetReminder={setReminder} isMobile={true} />
            ))}
          </div>
        )}

        {/* Floating Blur + Button */}
        {packs.length > 2 && (
          <div className="relative w-full flex justify-center items-center mt-[-10px] mb-6">
            {/* BACKGROUND BLUR BEHIND BUTTON (just like screenshot) */}
            {!isAccordionOpen && <div className="absolute inset-0 rounded-full"></div>}

            {/* BUTTON */}
            <button
              onClick={() => setIsAccordionOpen(!isAccordionOpen)}
              className="
          relative z-10
          px-6 py-3
          rounded-full
          bg-[#e8eeff] 
          text-[#3056ff]
          font-medium text-sm
          flex items-center gap-2
          shadow-md
          active:scale-95
          transition-all
        "
            >
              {isAccordionOpen ? 'Show Less' : `View Other Test Series (+${packs.length - 2})`}

              <ArrowDownIcon
                size={16}
                className={`transform transition-transform ${isAccordionOpen ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExploreTestSeries;
